import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  BAND_ACTIONS,
  IScheduleBlockEntryParams,
  IEventBlockParams,
} from "Bands/types";
import { EventScheduleBlockService } from "Bands/services";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";

export const dispatchGetEventScheduleBlocks = (
  dispatch: Dispatch<any>,
  event?: string
) => {
  dispatch(
    getEventScheduleBlocks({
      event,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventScheduleBlocks = createAsyncThunk(
  BAND_ACTIONS.getEventScheduleBlocks,
  async (
    {
      event,
      itemsPerPage,
      page,
    }: {
      event?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventScheduleBlockService.getBlocks({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        page,
        itemsPerPage,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventScheduleBlock]: data.eventScheduleBlock,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventScheduleBlock = (
  dispatch: Dispatch<any>,
  blockId: string
) => {
  const action = API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(blockId);
  return dispatch(getEventScheduleBlock({ blockId, action }));
};

export const getEventScheduleBlock = createAsyncThunk(
  BAND_ACTIONS.getEventScheduleBlock,
  async (
    { blockId }: { blockId: string; action: string },
    { rejectWithValue }
  ) => {
    try {
      const block = await EventScheduleBlockService.getBlock(blockId);

      return block;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventScheduleBlock = createAsyncThunk(
  BAND_ACTIONS.createEventScheduleBlock,
  async (block: IEventBlockParams, { rejectWithValue }) => {
    try {
      const data = await EventScheduleBlockService.createBlock(block);
      const id = Object.values(data?.eventScheduleBlock)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventScheduleBlockTitle = createAsyncThunk(
  BAND_ACTIONS.editEventScheduleBlockTitle,
  async (props: { id: string; title: string }, { rejectWithValue }) => {
    try {
      const data = await EventScheduleBlockService.editBlockTitle(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventScheduleBlock = createAsyncThunk(
  BAND_ACTIONS.editEventScheduleBlock,
  async (
    props: { id: string; data: IEventBlockParams },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventScheduleBlockService.editBlock(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventScheduleBlock = createAsyncThunk(
  BAND_ACTIONS.deleteEventScheduleBlock,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventScheduleBlockService.deleteBlock(id);
      return {
        path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(id),
        dataKey: IDataKeys.eventScheduleBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventScheduleBlockEntries = (
  dispatch: Dispatch<any>,
  block?: string
) => {
  dispatch(
    getEventScheduleBlockEntries({
      block,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventScheduleBlockEntries = createAsyncThunk(
  BAND_ACTIONS.getEventScheduleBlockEntries,
  async (
    {
      block,
      itemsPerPage,
      page,
    }: {
      block?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventScheduleBlockService.getBlocks({
        block: block ? API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(block) : undefined,
        page,
        itemsPerPage,
      });

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventScheduleBlockEntry = createAsyncThunk(
  BAND_ACTIONS.createEventScheduleBlockEntry,
  async (entry: IScheduleBlockEntryParams, { rejectWithValue }) => {
    try {
      const data = await EventScheduleBlockService.createEntry(entry);
      const id = Object.values(data?.eventScheduleTimesheetEntry)?.[0]
        ?.attributes?.id;

      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventScheduleBlockEntry = createAsyncThunk(
  BAND_ACTIONS.editEventScheduleBlockEntry,
  async (
    props: { id: string; data: IScheduleBlockEntryParams },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventScheduleBlockService.editEntry(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventScheduleBlockEntry = createAsyncThunk(
  BAND_ACTIONS.deleteEventScheduleBlockEntry,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventScheduleBlockService.deleteEntry(id);
      return {
        path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK_ENTRY(id),
        dataKey: IDataKeys.eventScheduleTimesheetEntry,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
